import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import client, { imgUrl } from "@/lib/glossyApi";
import MagazineLayout from "@/components/MagazineLayout";
import Markdown from "@/components/Markdown";
import ArticleSpread from "@/components/ArticleSpread";
import { Reveal } from "@/components/Reveal";
import MiniAd from "@/components/MiniAd";
import { MemberContactBlock } from "@/components/MemberContactBlock";
import Seo from "@/components/Seo";
import ListenButton from "@/components/ListenButton";
import { ArrowLeft } from "lucide-react";

// Keep reading must always show DIFFERENT people — one story per member, never the member you just read.
function pickKeepReading(list, currentSlug, currentMemberId) {
  const seen = new Set();
  const picks = [];
  for (const a of list) {
    if (a.slug === currentSlug) continue;
    if (currentMemberId && a.member_id === currentMemberId) continue;
    const key = a.member_id || a.slug;
    if (seen.has(key)) continue;
    seen.add(key);
    picks.push(a);
    if (picks.length >= 4) break;
  }
  return picks;
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const pt = searchParams.get("pt");
  const [article, setArticle] = useState(null);
  const [more, setMore] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    client.get(`/articles/${slug}${pt ? `?pt=${pt}` : ""}`).then((r) => {
      setArticle(r.data);
      document.title = `${r.data.title} — S/ME`;
    }).catch(() => setNotFound(true));
    client.get("/articles?limit=24").then((r) => setMore(r.data)).catch(() => {});
  }, [slug]);

  if (notFound)
    return (
      <MagazineLayout>
        <div className="h-screen flex flex-col items-center justify-center gap-4">
          <p className="font-serif text-4xl">Story not found</p>
          <Link to="/read" className="overline link-underline">Back to The Edit</Link>
        </div>
      </MagazineLayout>
    );

  if (!article)
    return <MagazineLayout><div className="h-screen flex items-center justify-center"><p className="overline text-ink/40">Loading…</p></div></MagazineLayout>;

  const backTo = article.type === "editorial" ? "/newsroom" : "/read";
  const backLabel = article.type === "editorial" ? "The Newsroom" : "The Edit";
  return (
    <MagazineLayout darkNav>
      <Seo
        title={`${article.seo_title || article.title} — S/ME`}
        description={article.seo_description || article.dek}
        image={imgUrl(article)}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.seo_description || article.dek,
          image: imgUrl(article),
          author: { "@type": "Organization", name: article.author || "S/ME" },
          publisher: { "@type": "Organization", name: "S/ME" },
          articleSection: article.category,
        }}
      />
      <article data-testid="article-page">
        {/* Hero */}
        <header className="relative h-[78vh] w-full overflow-hidden grain">
          <img src={imgUrl(article)} alt={article.title} className={`absolute inset-0 w-full h-full object-cover ${article.hero_fit === "headroom" ? "object-top" : "object-[center_22%]"} ed-grade animate-kenburns`} style={article.hero_focus ? { objectPosition: article.hero_focus } : undefined} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/40" />
          <div className="absolute inset-0 flex items-end z-10">
            <div className="max-w-[1100px] mx-auto w-full px-5 sm:px-8 pb-16">
              <div className="flex items-center gap-3 mb-5">
                <p className="overline text-gloss-gold">{article.category} · {article.type === "member_feature" ? "Member Feature" : article.type === "five_questions" ? "5 Questions" : article.type === "blog" ? "Members' Desk" : "Editorial"}</p>
                {article.accent && <span className="inline-block h-3 w-8" style={{ backgroundColor: article.accent }} data-testid="accent-bar" />}
              </div>
              <h1 className="font-serif text-cream text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl">{article.title}</h1>
              <p className="font-sans text-cream/80 text-lg sm:text-xl mt-5 max-w-2xl leading-relaxed">{article.dek}</p>
              <p className="font-mono text-xs text-cream/50 mt-6 uppercase tracking-widest">By {article.author}</p>
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="bg-cream py-16 sm:py-24 px-5 sm:px-8">
          <div className="max-w-[760px] mx-auto mb-10">
            <ListenButton slug={article.slug} accent={article.accent} />
          </div>
          {article.gallery?.length > 0 ? (
            <ArticleSpread body={article.body} gallery={article.gallery} title={article.title} accent={article.accent} />
          ) : (
            <div className="max-w-[760px] mx-auto">
              <Reveal>
                <Markdown text={article.body} accent={article.accent} />
              </Reveal>
            </div>
          )}
          <div className="max-w-[760px] mx-auto">
            {article.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-ink/15">
                {article.tags.map((t) => (
                  <span key={t} className="font-mono text-xs uppercase tracking-wider border border-ink/30 px-3 py-1.5">#{t}</span>
                ))}
              </div>
            )}
            {article.member_id && !article.hide_contact && <MemberContactBlock memberId={article.member_id} accent={article.accent} />}
            <Link to={backTo} className="flex items-center gap-2 mt-12 overline link-underline"><ArrowLeft size={14} /> Back to {backLabel}</Link>
            {article.member_id && (
              <div className="mt-12 border-t border-b border-ink/15 py-5 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2" data-testid="member-next-strip">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/50">This feature was created for an S/ME member</p>
                <Link to="/join" className="overline link-underline">Your business could be next →</Link>
              </div>
            )}
          </div>
          {/* small square advert */}
          <div className="max-w-[760px] mx-auto mt-16 flex justify-center">
            <MiniAd variant="square" space="article_square" excludeMemberId={article.member_id} className="w-full max-w-sm" />
          </div>
          {/* slim strip advert */}
          <div className="max-w-[760px] mx-auto mt-6">
            <MiniAd variant="banner" mode="image" space="article_banner" excludeMemberId={article.member_id} />
          </div>
        </div>

        {/* More */}
        {more.length > 0 && (
          <section className="bg-ink text-cream py-20 px-5 sm:px-8 grain">
            <div className="max-w-[1600px] mx-auto relative z-10">
              <h2 className="font-grotesk text-2xl sm:text-3xl font-medium border-b border-cream/20 pb-5 mb-10">Keep reading</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {pickKeepReading(more, slug, article.member_id).map((a) => (
                  <Link key={a.id} to={`/read/${a.slug}`} className="group block" data-testid="keep-reading-card">
                    <div className="relative overflow-hidden aspect-[3/2] bg-void ed-frame">
                      <img src={imgUrl(a)} alt={a.title} className="w-full h-full object-cover ed-grade transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <p className="overline text-gloss-gold mt-4">{a.category}</p>
                    <h3 className="font-serif text-xl tracking-tight mt-1.5 leading-tight">{a.title}</h3>
                    {a.member_name && (
                      <p className="font-sans text-sm text-cream/60 mt-1.5" data-testid="keep-reading-name">{a.member_name}{a.member_business ? ` · ${a.member_business}` : ""}</p>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </MagazineLayout>
  );
}
